import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Platform } from '@ionic/angular';
import { ATTACHMENT_LOCAL_PREFIX } from '@services/attachment/attachment-service.interface';
import { AuditNodeViewModel } from 'src/app/business/swodoc/view-models/audit-node-view-model';
import { v4 } from 'uuid';
import { IAttachmentWithData } from '../../document-attachment.service';
import { DocumentEditService } from '../../document-edit.service';
import { FabricModalService } from './fabric-modal/fabric.service';
import { SwodocNodeWoundMeasureService } from './swodoc-node-wound-measure.service';

@Component({
  selector: 'swodoc-node-wound-measure',
  templateUrl: 'swodoc-node-wound-measure.html',
  styleUrls: ['swodoc-node-wound-measure.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SwodocNodeWoundMeasureService],
})
export class SwodocNodeWoundMeasure implements OnInit {
  @Input() node: AuditNodeViewModel;
  @Input() auditNodes: AuditNodeViewModel[];
  @Input() documentEditService: DocumentEditService;
  @Output() changed = new EventEmitter<AuditNodeViewModel>();

  @ViewChild('file', { static: true })
  public fileInputElementRef: ElementRef;

  public images: Partial<IAttachmentWithData>[];
  @ViewChild('markerImageElement', { static: true })
  public markerImageElementRef: ElementRef;
  public markerImage: string;

  public selectedFile: File;

  constructor(
    private _changeDetectionRef: ChangeDetectorRef,
    private _fabricModal: FabricModalService,
    private _platform: Platform,
    private _swodocNodeImageService: SwodocNodeWoundMeasureService
  ) {}

  ngOnInit() {
    this.images = this.documentEditService.getImagesForNodeId(this.node.id);
  }

  public async addImage(): Promise<void> {
    if (this._platform.is('hybrid')) {
      return this._swodocNodeImageService.showSelectImageActionSheet(this._addImageToImagesArray.bind(this));
    }
    this.fileInputElementRef.nativeElement.click();
  }

  public async handleFileOpenDialog(event: Event): Promise<void> {
    const image = await this._swodocNodeImageService.fileAsBase64((event.target as any).files[0]);
    this._addImageToImagesArray(image, false);
    this.selectFile(event);
    // Clear the filename input in order to allow upload of the same file multiple times.
    // Otherwise the change event would not fire
    (event.target as any).value = null;
  }

  public selectFile(event) {
    this.selectedFile = event.target.files[0];
  }

  public deleteImage() {
    // reset images
    this.images.forEach(img => {
      this.documentEditService.removeImage(img._id);
    });
    this.images = [];
    this.images = [];
    this.selectedFile = null;
  }

  public async showEditMeasureModal() {
    // edit case
    let input: Partial<IAttachmentWithData>;
    if (!this.selectedFile && this.images[0]) {
      input = this.images[0];
    } else {
      input = {
        data: (await this._toBase64()).toString(),
      };
    }
    await this._fabricModal.show(input, this.node, this.auditNodes, this.documentEditService, true);
    this.changed.emit(this.node);
  }

  private _toBase64(): Promise<string | ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(this.selectedFile);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }

  private async _addImageToImagesArray(image, showMarker?: boolean) {
    const imageToSave = image;
    if (imageToSave) {
      const newImage = {
        _id: `${ATTACHMENT_LOCAL_PREFIX}${v4()}`,
        metadata: {
          auditNodeId: this.node.id,
          type: 100, // SwodocImage
        },
        data: imageToSave,
      } as Partial<IAttachmentWithData>;
      this.documentEditService.addImage(newImage);
      this.images.push(newImage);
      this._changeDetectionRef.markForCheck();
    }
  }
}
